import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { FullProject as Project } from '../../types';
import { getCategoriesFromMetadata, ProjectCategory } from '../ProjectOverviewModal';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  SYSTEM_PROJECT_IDS,
  SYSTEM_PROJECT_NAMES,
  GLOBAL_VIEW_IDS,
  PERSISTENT_PROJECT_VIEWS
} from './types';

export function useProjectSidebar() {
  const { 
    projects, 
    currentProjectId, 
    setCurrentProjectId,
    addProject, 
    archiveProject, 
    unarchiveProject, 
    reorderProjects: onReorderProjects,
    updateProjectCategory: onUpdateProjectCategory,
    allProjects,
    activeWorkspace,
    setActiveWorkspace: onWorkspaceChange,
    globalMetadata
  } = useProject();

  const {
    viewMode,
    setViewMode,
    isSidebarCollapsed: isCollapsed,
    setIsSidebarCollapsed: setIsCollapsed,
    searchQuery,
    searchResults,
    setProjectToDuplicate: onDuplicateProject,
    setProjectToDelete: onDeleteProject,
    setProjectToRename: onRenameProject,
    showProjectOverviewModal,
    uiMode
  } = useUI();

  const { user } = useAuth();

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const isCreatingRef = useRef(false);

  // Memoized System Project Check
  const isSystemProject = useCallback((p: Project) => {
    const id = p.id.toLowerCase();
    return (SYSTEM_PROJECT_IDS as readonly string[]).includes(id) || 
      id.startsWith('inbox_inforum') ||
      id.startsWith('comm_internal') ||
      (SYSTEM_PROJECT_NAMES as readonly string[]).some(name => p.name.toLowerCase().includes(name));
  }, []);

  // Project search & matching logic
  const searchMatchProjectIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set<string>();
    return new Set(searchResults.map(t => t.projectId).filter(Boolean) as string[]);
  }, [searchResults, searchQuery]);

  const filteredProjects = useMemo(() => {
    if (searchQuery.trim() === '') return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter(p => 
      p.name.toLowerCase().includes(query) || 
      searchMatchProjectIds.has(p.id)
    );
  }, [projects, searchQuery, searchMatchProjectIds]);

  // Memoized Project Filtering (Only active projects)
  const activeProjects = useMemo(() => 
    filteredProjects.filter(p => !p.archived && !isSystemProject(p)),
    [filteredProjects, isSystemProject]);

  // Load categories from globalMetadata (workspace-specific)
  const [categories, setCategories] = useState<ProjectCategory[]>(() => getCategoriesFromMetadata(globalMetadata, activeWorkspace));
  useEffect(() => {
    setCategories(getCategoriesFromMetadata(globalMetadata, activeWorkspace));

    const handleStorageChange = () => {
      setCategories(getCategoriesFromMetadata(globalMetadata, activeWorkspace));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [activeWorkspace, showProjectOverviewModal, globalMetadata]);

  const visibleProjects = useMemo(() => activeProjects, [activeProjects]);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Optimistic Selection State
  const [localActiveId, setLocalActiveId] = useState<string>(currentProjectId);
  const [localViewMode, setLocalViewMode] = useState<string>(viewMode);

  useEffect(() => {
    setLocalActiveId(currentProjectId);
  }, [currentProjectId]);

  useEffect(() => {
    setLocalViewMode(viewMode);
  }, [viewMode]);

  const isStrategyActive = useMemo(() => 
    ['analysis', 'metricsDashboard', 'projectPool'].includes(localViewMode),
    [localViewMode]
  );
  const [strategyManuallyToggled, setStrategyManuallyToggled] = useState<boolean | null>(null);
  const isStrategyOpen = strategyManuallyToggled !== null ? strategyManuallyToggled : isStrategyActive;

  const isConceptView = useMemo(() => ['concept', 'conceptBuilder', 'report'].includes(viewMode), [viewMode]);

  const onSelectProject = useCallback((id: string) => {
    // Update project ID synchronously to prevent visual delays/freezes
    setCurrentProjectId(id);

    if (id === 'dashboard') { setViewMode('dashboard'); }
    else if (id === 'metricsDashboard') { setViewMode('metricsDashboard'); }
    else if (id === 'globalKanban') { setViewMode('globalKanban'); }
    else if (id === 'communication') { setViewMode('communication'); }
    else if (id === 'globalGantt') { setViewMode('globalGantt'); }
    else if (id === 'aiAssistant') { setViewMode('aiAssistant'); }
    else if (id === 'impactEffort' || id === 'analysis') { setViewMode('analysis'); }
    else if (id === 'noDateTasks') { setViewMode('globalKanban'); }
    else if (id === 'projectPool') { setViewMode('projectPool'); }
    else {
      const selectedProject = allProjects.find(p => p.id === id);
      if (selectedProject?.name.toLowerCase() === 'kommunikation') {
        setViewMode('communication');
      } else {
        // Force back to a standard view if coming from a global or concept view
        if ((GLOBAL_VIEW_IDS as readonly string[]).includes(viewMode) || isConceptView) {
          const lastView = localStorage.getItem(`gwantt_last_view_${id}`);
          setViewMode((lastView && (PERSISTENT_PROJECT_VIEWS as readonly string[]).includes(lastView)) ? lastView as any : 'gantt');
        }
      }
    }
  }, [allProjects, viewMode, setViewMode, setCurrentProjectId, isConceptView]);

  const handleSelect = useCallback((id: string) => {
    // Immediate visual feedback
    setLocalActiveId(id);
    if (['globalKanban', 'communication', 'dashboard', 'analysis', 'metricsDashboard', 'changelog', 'globalCalendar', 'aiAssistant', 'projectPool', 'globalGantt'].includes(id)) {
      setLocalViewMode(id);
    }
    // Heavy update
    onSelectProject(id);
  }, [onSelectProject]);

  const onAddProject = useCallback(async () => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setIsCreatingProject(true);
    try {
      const newProject = await addProject();
      if (newProject) {
        onRenameProject(newProject.id);
      }
    } finally {
      isCreatingRef.current = false;
      setIsCreatingProject(false);
    }
  }, [addProject, onRenameProject]);

  const onPrefetch = useCallback((view: string) => {
    switch(view) {
      case 'metricsDashboard': import('../MetricsDashboardView'); break;
      case 'ideaPool': import('../IdeaPoolView'); break;
      case 'communication': import('../CommunicationView'); break;
      case 'dashboard': import('../GlobalCalendarView'); break;
      case 'globalCalendar': import('../GlobalCalendarView'); break;
    }
  }, []);

  // Close menu on click outside
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = () => setOpenMenuId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const handleArchiveToggle = useCallback((id: string, isArchived: boolean) => {
    if (isArchived) unarchiveProject(id);
    else archiveProject(id);
  }, [unarchiveProject, archiveProject]);

  return {
    isCollapsed,
    setIsCollapsed,
    activeWorkspace,
    onWorkspaceChange,
    localViewMode,
    uiMode,
    isStrategyOpen,
    isStrategyActive,
    setStrategyManuallyToggled,
    handleSelect,
    onPrefetch,
    visibleProjects,
    localActiveId,
    openMenuId,
    setOpenMenuId,
    categories,
    user,
    onReorderProjects,
    onDuplicateProject,
    onRenameProject,
    handleArchiveToggle,
    onDeleteProject,
    onUpdateProjectCategory,
    isCreatingProject,
    onAddProject
  };
}
